// src/components/ProtectedRoute.tsx
import React, { useCallback } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ConnectionProvider } from "../context/ConnectionContext";
import ConnectionWrapper from "./ConnectionWrapper";
import Loading from "./common/Loading";
import { useSessionTimeout } from "../hooks/useSessionTimeout";
import SessionTimeoutWarning from "./SessionTimeoutWarning";

const ProtectedRoute: React.FC = () => {
  const {
    isAuthenticated,
    loading,
    mfaRequired,
    mfaVerified,
    mfaConfigured,
    resetAuthState,
  } = useAuth();
  const navigate = useNavigate();

  const handleTimeout = useCallback(async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch {}
    resetAuthState();
    navigate("/login?reason=inactivity");
  }, [resetAuthState, navigate]);

  const { showWarning, secondsRemaining, extendSession } = useSessionTimeout({
    enabled: isAuthenticated && (!mfaRequired || mfaVerified),
    onTimeout: handleTimeout,
  });

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // MFA gate: redirect to setup or challenge if MFA is required but not verified
  if (mfaRequired && !mfaVerified) {
    if (mfaConfigured) {
      return <Navigate to="/mfa-challenge" replace />;
    } else {
      return <Navigate to="/mfa-setup" replace />;
    }
  }

  return (
    <ConnectionProvider>
      <ConnectionWrapper>
        <Outlet />
        {showWarning && (
          <SessionTimeoutWarning
            secondsRemaining={secondsRemaining}
            onContinue={extendSession}
            onLogout={handleTimeout}
          />
        )}
      </ConnectionWrapper>
    </ConnectionProvider>
  );
};

export default ProtectedRoute;
